/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import gameActions from '@sportninja/common/actions/game'
import orgActions from '@sportninja/common/actions/org'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import req from '@sportninja/common/api/request'
import { makeGetGame } from '@sportninja/common/selectors/game'
import {
  getAllGenericSportProperties,
  getGameStatusTypesBySportId,
  getGameTypesBySportId,
  getSoccerSport,
  getSportById,
} from '@sportninja/common/selectors/types'
import {
  useReadWithPagination,
  disableLivestream,
} from '@sportninja/common/utils/utils'
import { push } from 'connected-react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, useLocation } from 'react-router-dom'
import { NewModal } from 'src/components/NewModal'
import { useHighlights } from '@sportninja/common/hooks/useHighlights'
import HighlightsV2 from '../../components/HighlightsV2'
import PageWrap from '../../components/PageWrap'
import { Mobile } from '../../components/Responsive'
import { backgroundColorLight } from '../../components/css'
import PlayerStatistics from '../../components/v2/PlayerStatistics'
import { GameModalContainer } from '../../features/game'
import { createSubPages } from '../utils'
import Details from './Details'
import GamePageHeader from './GamePageHeader'
import GameRosterUnified from './GameRosterUnified'
import Scoresheet from './Scoresheet'
import Timeline from './Timeline'

import appOrigin from '@sportninja/common/constants/appOrigin'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import DownloadAppModal from 'src/components/DownloadAppModal'
import GameStatusModal from 'src/components/GameStatusModal'
import OpenInAppModal from 'src/components/OpenInAppModal'
import BaseActionButton from 'src/components/Page/BaseActionButton'
import UploadVideoModal from 'src/components/UploadVideoModal'
import ShareIcon from 'src/components/icons/ShareIcon'
import GameStatusIcon from 'src/components/icons/GameStatusIcon'
import QRCodeIcon from 'src/components/icons/QRCodeIcon'
import SendToMobileIcon from 'src/components/icons/SendToMobileIcon'
import EditGameIcon from 'src/components/icons/EditGameIcon'
import NewGamePageHeader from './NewGamePageHeader'
import NewMobileGamePageHeader from './NewMobileGamePageHeader'
import SignReport from './SignInReport'
import { HeaderSwitchTransition } from 'src/components/Transitions'
import useGameLivestream from 'src/hooks/useGameLivestream'
import FreeVideoAccessModal from 'src/components/FreeVideoAccessModal'
import { isValidYoutubeUrl, isValidS3VideoUrl } from 'src/utils/video'
import VideographerModal from './VideoAssignment/VideographerModal'
import Livestreams from './Livestreams'
import { useIsUserVideographer } from './hooks/useIsUserVideographer'
import { useSidebarState } from 'src/hooks/useSidebarState'
import ShareModal from 'src/components/ShareModal'
import {
  trackVideoPlayerRetry,
  trackVideoReplayRequested,
  trackVideoStreamSelected,
  trackVideoFreeAccessModalViewed,
  trackVideoVideographerModalOpened,
} from 'src/utils/videoAnalytics'

const Game = ({
  isPublicRoute,
  backToParentSchedule,
  game,
  gameTypes,
  inFrame,
  match: {
    params: { id },
  },
  readGame,
  updateGame,
  deleteGame,
  readPublicGame,
  readOrg,
  isLoggedIn,
  isSoccer = false,
  genericSportProperties,
  sport,
  gameStatusTypes,
}) => {
  const activeRoute = useLocation().pathname

  // Game livestream hook for real-time broadcast state updates
  const {
    // Multi-stream state
    activeStreams: allActiveStreams,
    availableStreams: allAvailableStreams,
    selectedStreamType,
    setSelectedStreamType,
    selectedStream,
    // Legacy/computed values
    broadcastState,
    streamUrl: contextStreamUrl,
    error: channelError,
    wsHomeScore,
    wsVisitingScore,
    lastScoreUpdate,
    // WebSocket timeline events
    wsTimelineEvents,
    wsGameState,
    resetWsState,
    resetWsTimelineOnly,
    updateFromApi,
    // Cheers
    cheerEvents,
    clearCheerEvent,
  } = useGameLivestream(id, game?.livestreams)

  // Check if current user is a videographer for this game
  const { isUserVideographer } = useIsUserVideographer({
    userActiveGameRoles: game?.user_active_game_roles,
  })

  // Fetch highlights for Recap tab visibility
  const { data: highlightsData, refetch: refetchHighlights } = useHighlights(id)
  const hasHighlights = highlightsData?.length > 0

  useEffect(() => {
    if (id) {
      refetchHighlights()
    }
  }, [id, refetchHighlights])

  // Callback to refresh livestreams data (called after delete)
  const refreshLivestreams = useCallback(async () => {
    try {
      const response = await req(`/games/${id}/livestreams`)
      const livestreams = response.data || response || []
      updateFromApi(livestreams)
    } catch {
      // Silently fail - not all games have livestreams endpoint
    }
  }, [id, updateFromApi])

  // Use all streams (league, home, visitor) for web
  const activeStreams = allActiveStreams
  const activeStreamCount = activeStreams.length

  // Use all available streams (live OR recorded) for web
  const availableStreams = allAvailableStreams
  const availableStreamCount = availableStreams.length

  // Log when stream counts change (for debugging multi-stream)
  useEffect(() => {
    console.log('[Game] 🎬 Streams updated:', {
      activeStreamCount,
      availableStreamCount,
      activeStreams: activeStreams.map((s) => ({
        type: s.type,
        isLive: s.isLive,
      })),
      availableStreams: availableStreams.map((s) => ({
        type: s.type,
        isLive: s.isLive,
        hasRecording: s.hasRecording,
        recordingUrl: s.recordingUrl ? 'present' : 'missing',
      })),
      showSwitchButton: availableStreamCount >= 2,
      selectedStreamType,
      selectedStream: selectedStream
        ? {
            type: selectedStream.type,
            hasRecording: selectedStream.hasRecording,
            recordingUrl: selectedStream.recordingUrl ? 'present' : 'missing',
          }
        : null,
    })
  }, [
    activeStreamCount,
    availableStreamCount,
    activeStreams,
    availableStreams,
    selectedStreamType,
    selectedStream,
  ])

  /* useState */
  const [isOnTutorial] = useState(true)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [disableTabNavigation, setDisableTabNavigation] = useState(false)

  const scoreModalRef = useRef(null)
  const openInAppModalRef = useRef(null)
  const gameStatusModalRef = useRef(null)
  const videographerModalRef = useRef(null)

  // Video upload modal state
  const [isVideoUploadModalOpen, setIsVideoUploadModalOpen] = useState(false)

  // Video assignment refresh key
  const [videoAssignmentRefreshKey, setVideoAssignmentRefreshKey] = useState(0)

  // Free video access modal state
  const [isFreeVideoModalOpen, setIsFreeVideoModalOpen] = useState(false)

  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isEditGameOpen, setIsEditGameOpen] = useState(false)

  // Game scoring settings
  const [gameSettings, setGameSettings] = useState(null)
  const [watchingReplay, setWatchingReplay] = useState(false)

  useEffect(() => {
    setWatchingReplay(false)
  }, [broadcastState])

  const isMobile = useMemo(() => windowWidth < 768, [windowWidth])

  // Get sidebar collapsed state to calculate available content width
  const [sidebarCollapsed] = useSidebarState()
  const availableWidth = useMemo(() => {
    if (inFrame || isMobile || !isLoggedIn) {
      return windowWidth
    }
    const sidebarWidth = sidebarCollapsed ? 56 : 284
    return windowWidth - sidebarWidth
  }, [windowWidth, inFrame, isMobile, sidebarCollapsed, isLoggedIn])

  const hasPermission = useMemo(
    () => game?.permission?.update || game?.permission?.admin || false,
    [game]
  )
  const canViewHomeAttendanceInfo = useMemo(() => {
    if (game?.permission?.admin) {
      return true
    }
    if (game?.permission?.update) {
      return true
    }
    if (game?.permission?.score) {
      return true
    }
    if (game?.user_active_game_roles?.home_team?.is_team_official === true) {
      return true
    }
    if (game?.user_active_game_roles?.home_team?.is_player === true) {
      return true
    }
    return false
  }, [game])
  const canViewVisitorAttendanceInfo = useMemo(() => {
    if (game?.permission?.admin) {
      return true
    }
    if (game?.permission?.update) {
      return true
    }
    if (game?.permission?.score) {
      return true
    }
    if (
      game?.user_active_game_roles?.visiting_team?.is_team_official === true
    ) {
      return true
    }
    if (game?.user_active_game_roles?.visiting_team?.is_player === true) {
      return true
    }
    return false
  }, [game])
  const canScore = useMemo(
    () => game?.permission?.score || game?.permission?.admin,
    [game]
  )
  const hasVideographerPermission = useMemo(
    () =>
      game?.permission?.update ||
      game?.permission?.video ||
      game?.permission?.admin,
    [game]
  )
  const genericSportForGame = useMemo(() => {
    if (game?.generic_sport_id) {
      return genericSportProperties.find((s) => s.id === game.generic_sport_id)
    }
    return null
  }, [game, genericSportProperties])
  const isGenericGame = useMemo(
    () =>
      game?.generic_sport_id !== null && game?.generic_sport_id !== undefined,
    [game]
  )
  const gameShareUrl = useMemo(() => `${appOrigin}/game/${game.id}`, [game])

  // Create game object with WebSocket scores and game state prioritized over API data
  const gameWithLiveScore = useMemo(() => {
    const hasWsScores = wsHomeScore !== null || wsVisitingScore !== null
    const hasWsGameState =
      wsGameState?.clockTime || wsGameState?.periodNumber !== undefined

    if (!hasWsScores && !hasWsGameState) {
      return game
    }

    // Format clock time: "00:11:55" -> "11:55" (remove leading hours if 00)
    let formattedClockTime = wsGameState?.clockTime
    if (formattedClockTime && formattedClockTime.startsWith('00:')) {
      formattedClockTime = formattedClockTime.slice(3)
    }

    // Generate period name for "End of X" display when period not in progress
    const wsPeriodDisplayName = wsGameState?.periodName
      ? `End of ${wsGameState.periodName}`
      : null

    console.log('[Game] 🔄 Using WebSocket data:', {
      wsHomeScore,
      wsVisitingScore,
      wsClockTime: wsGameState?.clockTime,
      formattedClockTime,
      wsPeriodNumber: wsGameState?.periodNumber,
      wsPeriodName: wsGameState?.periodName,
      wsPeriodInProgress: wsGameState?.periodInProgress,
      apiHomeScore: game?.homeTeamScore,
      apiVisitingScore: game?.visitingTeamScore,
      apiCurrentPeriodTime: game?.currentPeriodTime,
      apiPeriodInProgress: game?.periodInProgress,
    })

    return {
      ...game,
      homeTeamScore: wsHomeScore ?? game?.homeTeamScore,
      visitingTeamScore: wsVisitingScore ?? game?.visitingTeamScore,
      // Update clock/period info from WebSocket
      currentPeriodTime: formattedClockTime ?? game?.currentPeriodTime,
      periodInProgress: wsGameState?.periodInProgress ?? game?.periodInProgress,
      currentPeriodName:
        wsGameState?.periodInProgress === false && wsPeriodDisplayName
          ? wsPeriodDisplayName
          : game?.currentPeriodName,
    }
  }, [game, wsHomeScore, wsVisitingScore, wsGameState])

  const isRecapRoute = useMemo(
    () => activeRoute?.includes('recap'),
    [activeRoute]
  )
  const isHomeTeamEmptyRoster = useMemo(() => !game.homeGameRosterId, [game])
  const isVisitingTeamEmptyRoster = useMemo(
    () => !game.visitingGameRosterId,
    [game]
  )
  const isHomeTeamPlaceholder = useMemo(
    () =>
      game &&
      game?.homeTeamSlot &&
      game?.homeTeamSlot.id &&
      game?.homeTeamSlot.team_id === null,
    [game]
  )
  const isVisitingTeamPlaceholder = useMemo(
    () =>
      game &&
      game?.visitingTeamSlot &&
      game?.visitingTeamSlot.id &&
      game?.visitingTeamSlot.team_id === null,
    [game]
  )
  const shouldDisplayGameStatus = useMemo(
    () => game && (hasPermission || canScore),
    [game, hasPermission, canScore]
  )

  // Feature flags consumption - only explicitly true means enabled
  const featureFlags = { livestreaming: true, 'ai-summary': true } //game?.feature_flags
  const hasValidFeatureFlags =
    featureFlags &&
    typeof featureFlags === 'object' &&
    !Array.isArray(featureFlags)
  const isLivestreamingEnabled =
    hasValidFeatureFlags && featureFlags.livestreaming === true
  const isAiSummaryEnabledByFlag =
    !hasValidFeatureFlags || featureFlags['ai-summary'] !== false
  const isAiSummaryEnabledBySettings =
    gameSettings?.settings?.ai?.summary?.enabled !== false
  const isAiSummaryEnabled =
    isAiSummaryEnabledByFlag && isAiSummaryEnabledBySettings
  const assignPointsToPlayer =
    gameSettings?.settings?.game?.scoring?.assign_points_to_player || false

  // Build PAGES object with correct tab order
  // Order: Overview, Rosters, Statistics, Highlights, Timeline, Scoresheet, Sign-In Sheet, Livestreams
  const PAGES = {
    details: 'Overview',
    roster: 'Rosters',
  }

  // Statistics tab - right after visitor-roster
  // Show for non-generic games, or generic games with assign_points_to_player enabled
  if (
    !isPublicRoute &&
    game.completed &&
    (!isGenericGame || assignPointsToPlayer)
  ) {
    PAGES.statistics = t('Web:statistics')
  }

  // Highlights tab - visible when highlights exist (public routes too)
  if (hasHighlights) {
    PAGES.recap = 'Highlights'
  }

  // Timeline is always shown
  PAGES.timeline = t('routeNames:timeline')

  PAGES.scoresheet = t('Web:scoresheet')

  // Scoresheet and Sign-In Sheet for non-public routes
  if (!isPublicRoute) {
    PAGES.signin = 'Sign-In Sheet'
  }

  // Livestreams tab - for users with videographer permission OR assigned as videographer
  if (
    !isPublicRoute &&
    !disableLivestream &&
    (hasVideographerPermission || isUserVideographer) &&
    isLivestreamingEnabled
  ) {
    PAGES.livestreams = 'Manage Livestreams'
  }

  const [subPages, routes] = createSubPages(ROUTES.GAME_ROOT, id, PAGES, {
    hasPermission: hasPermission || game.permission?.admin,
    privatePageCount: isPublicRoute ? 0 : 1,
  })

  const [loaded, error] = useReadWithPagination(readGame, id)

  // Check if broadcast_video_url is a YouTube URL (should show regardless of broadcast state)
  const isYoutubeVideo = isValidYoutubeUrl(game?.broadcast_video_url)
  // Check if broadcast_video_url is an S3 uploaded video (should show regardless of broadcast state)
  const isS3Video = isValidS3VideoUrl(game?.broadcast_video_url)

  const actions = useMemo(
    () => [
      // Free access info button - only for live Mux streams (moved to first position)
      game &&
        broadcastState === 'live' &&
        !isYoutubeVideo &&
        !isS3Video &&
        isLivestreamingEnabled && (
          <BaseActionButton
            title={'Free Access Info'}
            id='free-access-info-button'
            key='free-access-info'
            label={'Free Access Info'}
            customIcon={
              <svg
                xmlns='http://www.w3.org/2000/svg'
                height='18px'
                viewBox='0 -960 960 960'
                width='18px'
                fill='#e8eaed'
              >
                <path d='M444-200h70v-50q50-9 86-39t36-89q0-42-24-77t-96-61q-60-20-83-35t-23-41q0-26 18.5-41t53.5-15q32 0 50 15.5t26 38.5l64-26q-11-35-40.5-61T516-710v-50h-70v50q-50 11-78 44t-28 74q0 47 27.5 76t86.5 50q63 23 87.5 41t24.5 47q0 33-23.5 48.5T486-314q-33 0-58.5-20.5T390-396l-66 26q14 48 43.5 77.5T444-252v52Zm36 120q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z' />
              </svg>
            }
            onClick={() => {
              trackVideoFreeAccessModalViewed(game, {
                triggerSource: 'free_access_button',
              })
              setIsFreeVideoModalOpen(true)
            }}
            alwaysShowLabel={true}
          />
        ),
      game && (
        <BaseActionButton
          title={'Share Game'}
          id='share-game-button'
          key='share-game'
          label={'Share Game'}
          customIcon={<ShareIcon />}
          onClick={() => setIsShareModalOpen(true)}
          alwaysShowLabel={true}
        />
      ),
      shouldDisplayGameStatus && (
        <BaseActionButton
          title={'Game Status'}
          id='game-status-button'
          key='game-status'
          label={'Game Status'}
          customIcon={<GameStatusIcon />}
          onClick={async () => {
            gameStatusModalRef?.current?.openModal()
          }}
          alwaysShowLabel={true}
        />
      ),
      game && (
        <BaseActionButton
          title={'Open in App'}
          id='open-in-app-button'
          key='open-in-app'
          label={'Open in App'}
          customIcon={<QRCodeIcon />}
          onClick={async () => {
            openInAppModalRef?.current?.openModal()
          }}
          alwaysShowLabel={true}
        />
      ),
      // SN-4659 - Jeff request to add the isOnTutorial check here on Fri 13 Sep 2024
      game && canScore && !isCanlan && isOnTutorial && (
        <BaseActionButton
          title={'Score'}
          id='tutorial-score-button'
          key='score'
          label={t('Web:score')}
          customIcon={<SendToMobileIcon />}
          onClick={async () => {
            scoreModalRef?.current?.openModal()
          }}
          alwaysShowLabel={true}
        />
      ),
      game && hasPermission && (
        <BaseActionButton
          key='edit-game'
          onClick={() => setIsEditGameOpen(true)}
          title={t('Web:editGame')}
          customIcon={<EditGameIcon />}
        />
      ),
    ],
    [
      game,
      broadcastState,
      isYoutubeVideo,
      isS3Video,
      isLivestreamingEnabled,
      hasPermission,
      shouldDisplayGameStatus,
      canScore,
      isOnTutorial,
    ]
  )

  subPages?.forEach((page) => {
    page.actions = actions?.filter(Boolean)
  })

  const fullGameType = useMemo(
    () =>
      gameTypes?.find((gameType) => {
        return parseInt(gameType?.id, 10) === game?.gameType
      }),
    [gameTypes, game]
  )

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', handleResize)
    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  // Fetch game scoring settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await req(`/games/${id}/settings`)
        setGameSettings(data)
      } catch {
        // Settings may not be available for all games
      }
    }
    if (id && isLoggedIn) {
      fetchSettings()
    }
  }, [id, isLoggedIn])

  // Refetch game data when score update received and game is not started
  // This ensures we get updated period/time info when the game starts
  useEffect(() => {
    if (lastScoreUpdate && !game?.started) {
      console.log(
        '[Game] 🔄 Score update received but game not started, refetching...'
      )
      readGame(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScoreUpdate])

  // Check if replay is available (from API or WebSocket recording_ready event)
  const hasReplayUrl =
    !!game?.broadcast_video_url || !!selectedStream?.hasRecording
  // Determine if we should show video header (when live, ended, recorded streams available, or uploaded video exists)
  const shouldShowVideoHeader =
    isLivestreamingEnabled &&
    (broadcastState === 'live' ||
      broadcastState === 'ended' || // Show "Stream ended" overlay even without replay
      availableStreamCount > 0 ||
      isYoutubeVideo ||
      isS3Video)
  // Get effective stream (selected or first available)
  const effectiveStream = selectedStream || availableStreams[0] || null
  // Use effective stream URL for playback, fallback to stored URL
  const videoUrl =
    effectiveStream?.streamUrl ||
    effectiveStream?.recordingUrl ||
    contextStreamUrl ||
    game?.broadcast_video_url

  // Debug: log video URL changes
  useEffect(() => {
    console.log('[Game] 🎥 Video URL updated:', {
      effectiveStreamType: effectiveStream?.type,
      effectiveStreamUrl: effectiveStream?.streamUrl ? 'present' : 'missing',
      effectiveRecordingUrl: effectiveStream?.recordingUrl
        ? 'present'
        : 'missing',
      finalVideoUrl: videoUrl ? videoUrl.substring(0, 50) + '...' : 'none',
    })
  }, [effectiveStream, videoUrl])

  // Handler to retry loading the stream
  const handleRetry = useCallback(() => {
    trackVideoPlayerRetry(game, { broadcastState })
    readGame(id)
  }, [readGame, id, game, broadcastState])

  // Handler to watch replay
  const handleWatchReplay = useCallback(() => {
    trackVideoReplayRequested(game, { streamType: selectedStreamType })
    setWatchingReplay(true)
    readGame(id)
  }, [readGame, id, game, selectedStreamType])

  // Handler for stream selection
  const handleStreamSelection = useCallback(
    (streamType) => {
      const selectedStreamObj = availableStreams?.find(
        (s) => s.type === streamType
      )
      trackVideoStreamSelected(game, {
        selectedStreamType: streamType,
        previousStreamType: selectedStreamType,
        isStreamLive: selectedStreamObj?.isLive || false,
        hasRecording: selectedStreamObj?.hasRecording || false,
        isSwitch: !!selectedStreamType && selectedStreamType !== streamType,
      })
      setSelectedStreamType(streamType)
    },
    [setSelectedStreamType, game, selectedStreamType, availableStreams]
  )

  const getHeader = useCallback(() => {
    const displayVideoDesktop =
      shouldShowVideoHeader && !isMobile && !inFrame && !isRecapRoute
    const displayVideoMobile =
      shouldShowVideoHeader && isMobile && !inFrame && !isRecapRoute
    if (displayVideoDesktop) {
      return {
        key: 'video-desktop',
        element: (
          <NewGamePageHeader
            videoUrl={videoUrl}
            broadcastState={broadcastState}
            game={gameWithLiveScore}
            organization={
              game?.rootSchedule?.organization_id
                ? {
                    id: game?.rootSchedule?.organization_id,
                    name: game?.organization_name,
                  }
                : null
            }
            actions={inFrame ? [] : actions}
            fullGameType={fullGameType}
            isMobile={isMobile}
            windowWidth={availableWidth}
            onRetry={handleRetry}
            onWatchReplay={handleWatchReplay}
            hasReplayUrl={hasReplayUrl}
            watchingReplay={watchingReplay}
            error={channelError}
            activeStreamCount={availableStreamCount}
            onSelectStream={handleStreamSelection}
            selectedStreamType={selectedStreamType}
            homeTeamName={game?.homeTeam?.fullName || game?.homeTeamName}
            visitorTeamName={
              game?.visitingTeam?.fullName || game?.visitingTeamName
            }
            organizationName={game?.organization_name}
            streams={availableStreams}
            homeTeamImage={game?.homeTeam?.imageId}
            visitorTeamImage={game?.visitingTeam?.imageId}
            wsTimelineEvents={wsTimelineEvents}
            wsGameState={wsGameState}
            cheerEvents={cheerEvents}
            clearCheerEvent={clearCheerEvent}
          />
        ),
      }
    }
    if (displayVideoMobile) {
      return {
        key: 'video-mobile',
        element: (
          <NewMobileGamePageHeader
            videoUrl={videoUrl}
            broadcastState={broadcastState}
            game={gameWithLiveScore}
            organization={
              game?.rootSchedule?.organization_id
                ? {
                    id: game?.rootSchedule?.organization_id,
                    name: game?.organization_name,
                  }
                : null
            }
            actions={inFrame ? [] : actions}
            fullGameType={fullGameType}
            isMobile={isMobile}
            windowWidth={availableWidth}
            onRetry={handleRetry}
            onWatchReplay={handleWatchReplay}
            hasReplayUrl={hasReplayUrl}
            watchingReplay={watchingReplay}
            error={channelError}
            activeStreamCount={availableStreamCount}
            onSelectStream={handleStreamSelection}
            selectedStreamType={selectedStreamType}
            streams={availableStreams}
            homeTeamName={game?.homeTeam?.fullName || game?.homeTeamName}
            visitorTeamName={
              game?.visitingTeam?.fullName || game?.visitingTeamName
            }
            homeTeamImage={game?.homeTeam?.imageId}
            visitorTeamImage={game?.visitingTeam?.imageId}
            organizationName={game?.organization_name}
            wsTimelineEvents={wsTimelineEvents}
          />
        ),
      }
    }
    return {
      key: 'default',
      element: (
        <GamePageHeader
          genericSportForGame={genericSportForGame}
          error={error}
          actions={inFrame ? [] : actions}
          game={gameWithLiveScore}
          fullGameType={fullGameType}
          inFrame={inFrame}
          organization={
            game?.rootSchedule?.organization_id
              ? {
                  id: game?.rootSchedule?.organization_id,
                  name: game?.organization_name,
                }
              : null
          }
          disabled={disableTabNavigation}
          isSoccer={isSoccer}
          sport={sport}
        />
      ),
    }
  }, [
    shouldShowVideoHeader,
    videoUrl,
    broadcastState,
    game,
    gameWithLiveScore,
    isMobile,
    inFrame,
    isRecapRoute,
    genericSportForGame,
    error,
    actions,
    fullGameType,
    disableTabNavigation,
    isSoccer,
    sport,
    availableWidth,
    handleRetry,
    handleWatchReplay,
    hasReplayUrl,
    channelError,
    availableStreamCount,
    handleStreamSelection,
    availableStreams,
    selectedStreamType,
    watchingReplay,
    wsTimelineEvents,
    wsGameState,
    cheerEvents,
    clearCheerEvent,
  ])

  const { key: headerKey, element: headerElement } = getHeader()

  return (
    <>
      <PageWrap
        actions={actions}
        extraOffset={54}
        error={error}
        errorTitle='GAME UNAVAILABLE'
        errorMessage={
          error?.status === 403
            ? t('common:xIsPrivate', { x: t('common:game') })
            : error?.message
        }
        loaded={loaded}
        pages={subPages}
        navigationDisabled={disableTabNavigation}
        pageHeader={
          <HeaderSwitchTransition headerKey={headerKey}>
            {headerElement}
          </HeaderSwitchTransition>
        }
      >
        <Mobile>
          {(isMobileLayout) => {
            return (
              <div
                css={
                  isMobileLayout
                    ? css`
                        position: relative;
                        margin-top: -40px;
                        padding-top: 40px;
                        background-color: ${inFrame
                          ? 'transparent'
                          : backgroundColorLight};
                      `
                    : css`
                        margin-top: -24px;
                      `
                }
              >
                <Switch>
                  <Route
                    exact
                    path={routes.details}
                    render={(props) => (
                      <Details
                        game={game}
                        {...props}
                        isSoccer={isSoccer}
                        isGenericGame={isGenericGame}
                        canUpdateGame={hasPermission}
                        isMobile={isMobileLayout}
                        statsUrl={routes.statistics}
                        isPublicRoute={isPublicRoute}
                        isAiSummaryEnabled={isAiSummaryEnabled}
                      />
                    )}
                  />

                  <Route
                    exact
                    path={routes.timeline}
                    render={(props) => (
                      <Timeline
                        gameId={game.id}
                        isPublicRoute={isPublicRoute}
                        setDisableTabNavigation={setDisableTabNavigation}
                        isSoccer={isSoccer}
                        sportId={game?.sport_id}
                        isGenericGame={isGenericGame}
                        canUpdateGame={hasPermission}
                        readGame={readGame}
                        wsTimelineEvents={wsTimelineEvents}
                        wsGameState={wsGameState}
                        resetWsState={resetWsState}
                        resetWsTimelineOnly={resetWsTimelineOnly}
                        {...props}
                      />
                    )}
                  />

                  <Route
                    exact
                    path={routes.roster}
                    render={(props) => (
                      <GameRosterUnified
                        isPublicRoute={isPublicRoute}
                        game={game}
                        isHomeTeamPlaceholder={isHomeTeamPlaceholder}
                        isVisitingTeamPlaceholder={isVisitingTeamPlaceholder}
                        isHomeTeamEmptyRoster={isHomeTeamEmptyRoster}
                        isVisitingTeamEmptyRoster={isVisitingTeamEmptyRoster}
                        isGenericGame={isGenericGame}
                        canViewHomeAttendanceInfo={canViewHomeAttendanceInfo}
                        canViewVisitorAttendanceInfo={
                          canViewVisitorAttendanceInfo
                        }
                        {...props}
                      />
                    )}
                  />
                  <Route
                    exact
                    path={routes.scoresheet}
                    render={() => (
                      <Scoresheet gameNumber={game.gameNumber} id={id} />
                    )}
                  />
                  <Route
                    exact
                    path={routes.signin}
                    render={() => (
                      <SignReport gameNumber={game.gameNumber} id={id} />
                    )}
                  />
                  {!isPublicRoute &&
                    !disableLivestream &&
                    isLivestreamingEnabled &&
                    (hasVideographerPermission || isUserVideographer) && (
                      <Route
                        exact
                        path={routes.livestreams}
                        render={() => (
                          <Livestreams
                            gameId={id}
                            homeTeamName={
                              game?.homeTeam?.fullName || game?.homeTeamName
                            }
                            visitorTeamName={
                              game?.visitingTeam?.fullName ||
                              game?.visitingTeamName
                            }
                            onLivestreamDeleted={refreshLivestreams}
                            homeRosterId={game.homeGameRosterId}
                            visitorRosterId={game.visitingGameRosterId}
                            homeTeamImage={game?.homeTeam?.imageId}
                            visitorTeamImage={game?.visitingTeam?.imageId}
                            hasVideographerPermission={
                              hasVideographerPermission
                            }
                            refreshKey={videoAssignmentRefreshKey}
                            onAddLivestreamClick={() => {
                              setIsVideoUploadModalOpen(true)
                            }}
                            onAddVideographerClick={() => {
                              trackVideoVideographerModalOpened(game)
                              videographerModalRef?.current?.openModal()
                            }}
                          />
                        )}
                      />
                    )}
                  {!isPublicRoute &&
                    game.completed &&
                    (!isGenericGame || assignPointsToPlayer) && (
                      <Route
                        exact
                        path={routes.statistics}
                        render={() => (
                          <PlayerStatistics
                            id={id}
                            homeTeamId={game.homeTeamId}
                            visitingTeamId={game.visitingTeamId}
                            isSoccer={isSoccer}
                            defaultSortOrder={isSoccer ? 'G' : 'P'}
                            isInFrame={inFrame}
                            sport={sport}
                            isGenericGame={isGenericGame}
                          />
                        )}
                      />
                    )}
                  <Route
                    exact
                    path={routes.recap}
                    render={() => {
                      return (
                        <HighlightsV2
                          game={game}
                          highlightsData={highlightsData}
                        />
                      )
                    }}
                  />
                </Switch>
              </div>
            )
          }}
        </Mobile>
      </PageWrap>
      <NewModal ref={openInAppModalRef} shouldCloseOnOverlayClick={true}>
        <OpenInAppModal
          onPressClose={() => {
            openInAppModalRef?.current?.closeModal()
          }}
          gameId={game?.id}
          isTutorial={false}
        />
      </NewModal>
      <NewModal ref={scoreModalRef} shouldCloseOnOverlayClick={true}>
        <DownloadAppModal
          onPressClose={() => {
            scoreModalRef?.current?.closeModal()
          }}
          gameId={game?.id}
          isTutorial={true}
        />
      </NewModal>
      {game && hasPermission && (
        <GameModalContainer
          isOpen={isEditGameOpen}
          onClose={() => setIsEditGameOpen(false)}
          mode='edit'
          game={game}
          scheduleId={game?.rootSchedule?.id}
          organizationId={game?.rootSchedule?.organization_id}
          organizationName={game?.rootSchedule?.organization?.name_full}
          sportId={game?.sport_id}
          /*
           * `readGame` still runs after every save. The mutation invalidates the
           * React Query caches, but the Game page and its subpages read the game
           * out of redux, and `read.success` overwrites that entry -- this is
           * what replaces the saga's `update.success` merge.
           */
          onSaved={() => readGame(game.id)}
          onDeleted={() => backToParentSchedule(game?.rootSchedule?.id)}
        />
      )}
      <NewModal ref={gameStatusModalRef} shouldCloseOnOverlayClick={true}>
        <GameStatusModal
          onPressClose={() => {
            gameStatusModalRef?.current?.closeModal()
          }}
          gameCompleted={game?.completed}
          gameId={game?.id}
          gameStatus={game?.gameStatus}
          gameStatusTypes={gameStatusTypes}
          readGame={readGame}
        />
      </NewModal>
      <UploadVideoModal
        isOpen={isVideoUploadModalOpen}
        onClose={() => setIsVideoUploadModalOpen(false)}
        gameId={game?.id}
        readGame={async () => await readGame(game.id)}
        broadcastVideoUrl={game?.broadcast_video_url}
      />
      <FreeVideoAccessModal
        isOpen={isFreeVideoModalOpen}
        onClose={() => setIsFreeVideoModalOpen(false)}
        game={game}
      />
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        entityType='game'
        entityName={`${
          game?.homeTeam?.fullName || game?.homeTeamName || 'Home'
        } vs ${
          game?.visitingTeam?.fullName || game?.visitingTeamName || 'Visitor'
        }`}
        shareUrl={gameShareUrl}
      />
      {hasVideographerPermission && (
        <VideographerModal
          ref={videographerModalRef}
          gameId={id}
          homeRosterId={game.homeGameRosterId}
          visitorRosterId={game.visitingGameRosterId}
          homeTeamName={game?.homeTeam?.fullName || game?.homeTeamName || ''}
          visitorTeamName={
            game?.visitingTeam?.fullName || game?.visitingTeamName || ''
          }
          onSuccess={() => setVideoAssignmentRefreshKey((prev) => prev + 1)}
          homeTeamImage={game?.homeTeam?.image || game?.homeTeamImage}
          visitorTeamImage={
            game?.visitingTeam?.image || game?.visitingTeamImage
          }
        />
      )}
    </>
  )
}

const makeMapStateToProps = () => {
  const getGameById = makeGetGame()

  const mapStateToProps = (state, props) => {
    const game = getGameById(
      state,
      props.match.params.id?.replace('?sandbox_tour=1', '')
    )
    const sportId = game?.sport_id
    const gameTypes = getGameTypesBySportId(state, sportId)
    const isSoccer = game?.sport_id === getSoccerSport(state)?.id
    const sport = getSportById(state, sportId)
    return {
      inFrame: state.auth.inFrame,
      isLoggedIn: !!state.auth.user?.id,
      game,
      gameTypes,
      isSoccer,
      genericSportProperties: getAllGenericSportProperties(state),
      sport,
      gameStatusTypes: getGameStatusTypesBySportId(state, sportId),
    }
  }

  return mapStateToProps
}

const mapDispatchToProps = (dispatch) => {
  return {
    backToParentSchedule: (scheduleId) =>
      dispatch(push(`${ROUTES.SCHEDULE_ROOT}/${scheduleId}`)),
    readGame: bindActionToPromise(dispatch, gameActions.read.request),
    readPublicGame: bindActionToPromise(
      dispatch,
      gameActions.readPublic.request
    ),
    updateGame: bindActionToPromise(dispatch, gameActions.update.request),
    deleteGame: bindActionToPromise(dispatch, gameActions.delete.request),
    readOrg: bindActionToPromise(dispatch, orgActions.read.request),
  }
}

export default connect(makeMapStateToProps, mapDispatchToProps)(Game)
